import type { CSSProperties } from 'react';
import { User as UserIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from 'helpers/cn';
import { avatarFill, avatarHue, userFullName, userInitials } from 'helpers/user-identity';

export type UserAvatarUser = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  avatar?: string | null;
  avatarColour?: string | null;
};

type UserAvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<UserAvatarSize, string> = {
  xs: 'size-5 text-[9px]',
  sm: 'size-7 text-nano',
  md: 'size-8 text-micro',
  lg: 'size-10 text-body',
};

const ICON_CLASS: Record<UserAvatarSize, string> = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-5',
};

type UserAvatarProps = {
  user: UserAvatarUser;
  size?: UserAvatarSize;
  /**
   * For a badge that stands in for the name: a hover tooltip, and the name as its
   * accessible label. Leave it off where the name is written beside the badge, which
   * then stays decorative so a screen reader does not say the name twice.
   */
  tooltip?: boolean;
  className?: string;
};

/**
 * Who someone is, as a photo or their initials on their own hue. Every place that
 * shows a person uses this, so a teammate looks the same in the header, the team
 * list, the experiment list and the activity log.
 */
export const UserAvatar = ({ user, size = 'md', tooltip = false, className }: UserAvatarProps) => {
  const name = userFullName(user);
  const initials = userInitials(user);
  const fillStyle = { '--avatar-fill': avatarFill(avatarHue(user)) } as CSSProperties;

  // With a tooltip the badge is the only place the name is, so it has to be reachable
  // from the keyboard: focus opens the tooltip, as hover does.
  const badge = (
    <Avatar
      className={cn(
        'shrink-0',
        SIZE_CLASS[size],
        tooltip &&
          'ring-offset-background focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden',
        className,
      )}
      {...(tooltip ? { role: 'img', 'aria-label': name, tabIndex: 0 } : { 'aria-hidden': true })}
    >
      {!!user.avatar && <AvatarImage src={user.avatar} alt="" />}
      {/* bg-(--avatar-fill), not a custom utility: see avatarFill for why. */}
      <AvatarFallback className="bg-(--avatar-fill) font-semibold tracking-[0.02em] text-white" style={fillStyle}>
        {/* Trimmed to the cap height, so centring the box centres the letters. Untrimmed,
            the line box keeps room for descenders that capitals never use, and the
            initials sat 2px above the middle of a 28px badge. Browsers without text-box
            fall back to that. */}
        {initials ? (
          <span className="leading-none [text-box:trim-both_cap_alphabetic]">{initials}</span>
        ) : (
          <UserIcon className={ICON_CLASS[size]} strokeWidth={1.75} />
        )}
      </AvatarFallback>
    </Avatar>
  );

  if (!tooltip || !name) return badge;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className="text-meta px-2 py-1">{name}</TooltipContent>
    </Tooltip>
  );
};
